import * as vp from 'vopay';

import { FunctionComponent, useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import AccountContext from '../AccountContext';
import { usePartnerLogin } from '@/providers/PartnerLogin.provider';
import usePostSendOnboardingEmail from '@/hooks/useApi/usePostSendOnboardingEmail';
import SendOnboardingEmailDialogue from './AccountPanelDialogue';
import { SendOnboardingEmailDialogueHandler, DialogueHandlerProps } from './types';

const { Box, Button } = vp;

type AccountOnboardingPanelProps = {
  hasValidOnboardingUrl: boolean | null;
  onCopyClick: () => void;
  onActionClick: () => void;
  onBoardingLink: string | null;
};

type FC = FunctionComponent<AccountOnboardingPanelProps>;

const AccountOnboardingPanel: FC = ({
  hasValidOnboardingUrl,
  onActionClick,
  onCopyClick,
  onBoardingLink,
}: AccountOnboardingPanelProps) => {
  const {
    response: { isInsideSales, isRoleViewOnly },
  } = usePartnerLogin();
  const [copiedLabel, setCopiedLabel] = useState('Copy URL');

  const { details } = useContext(AccountContext);
  const { accountId } = useParams<{ accountId: string }>();
  const [message, setMessage] = useState('');
  const [dialogType, setDialogType] = useState('none');
  const [shouldShowAlert, setShouldShowAlert] = useState(false);
  const [onboardingEmail, postSendOnboardingEmail] = usePostSendOnboardingEmail({});

  const handleCopied = () => {
    setCopiedLabel('Copied!');
    onCopyClick();
  };

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<SendOnboardingEmailDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: SendOnboardingEmailDialogueHandler = {
        confirm: {
          success: {
            default: () => {
              setDialogType('');
              postSendOnboardingEmail({
                Name: details[accountId].accountName,
                Email: details[accountId].contact.email,
                Link: onBoardingLink || '',
                VopayAccountID: accountId,
              });
              setTimeout(() => {
                setShouldShowAlert(true);
              }, 1500);
            },
          },
          fail: {
            default: () => {
              setDialogType('');
            },
          },
        },
        send: {
          success: {
            default: () => {
              setDialogType('');
            },
          },
          fail: {
            default: () => {
              setDialogType('');
              setMessage(onboardingEmail.ErrorMessage);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  useEffect(() => {
    if (shouldShowAlert) {
      setShouldShowAlert(false);
      if (onboardingEmail.Success) {
        setDialogType('success');
      } else {
        setDialogType('fail');
      }
    }
  }, [onboardingEmail]);

  return (
    <Box flx="col" boundary="shadow" mb="lg" pt="md" pb="md" ps="md" pe="md" jc="sb" ai="fs">
      <Box jc="center" ai="center" pb="smd">
        Activate this account
      </Box>
      <Box gap="sm">
        <Button
          id="account-onboarding"
          label={copiedLabel}
          isDisabled={!hasValidOnboardingUrl || isRoleViewOnly}
          onClick={handleCopied}
          width="185px"
          height="28px"
        />
        <Button
          id="account-onboarding"
          label={getButtonLabel()}
          isDisabled={isRoleViewOnly}
          width="185px"
          height="28px"
          isSubmitting={hasValidOnboardingUrl === null}
          onClick={onActionClick}
        />
        {isInsideSales && (
          <Button
            id="send-onboarding-email"
            label="Send Application Form"
            width="185px"
            height="28px"
            isSubmitting={hasValidOnboardingUrl === null}
            isDisabled={hasValidOnboardingUrl === null || hasValidOnboardingUrl === false || isRoleViewOnly}
            onClick={() => {
              setDialogType('confirm');
            }}
          />
        )}
        <SendOnboardingEmailDialogue type={dialogType} subTitle={message} handleDialogue={handlers.handleDialogue} />
      </Box>
    </Box>
  );

  function getButtonLabel() {
    if (!hasValidOnboardingUrl) return 'Open Application Form';
    return hasValidOnboardingUrl === null ? '' : 'Get Onboarding URL';
  }
};

export default AccountOnboardingPanel;
